import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { api } from "../api";
import { VotesRequiredAPI } from "../../../src/apidefs";
import { RLink } from "../DesignSystem/RLink";
import { TestIds } from "../testIds";
import { CheckIcon, XMarkIcon } from "@heroicons/react/24/solid";

type CompoRequiredVotesParams = { id: string };

export const CompoRequiredVotesPage = (): JSX.Element => {
  const { id } = useParams<CompoRequiredVotesParams>();
  const [requiredVotes, setRequiredVotes] = useState<VotesRequiredAPI>();

  useEffect(() => {
    let cancelled = false;

    const fetchRequiredVotes = async () => {
      const result = await api.get<"/api/compo/:id/admin/votesrequired">(
        `/api/compo/${id}/admin/votesrequired`
      );

      if (!api.isToast(result) && !cancelled) {
        setRequiredVotes({
          ...result,
          aggOnEntry: result.aggOnEntry || {},
          aggOnUser: result.aggOnUser || {},
        });
      }
    };

    fetchRequiredVotes();

    return () => {
      cancelled = true;
    };
  }, [id]);

  if (!requiredVotes) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-100 dark:bg-slate-900">
        <div className="text-slate-500 dark:text-slate-400">Loading...</div>
      </div>
    );
  }

  const entryIds = Object.keys(requiredVotes.aggOnEntry || {}).map((value) =>
    Number(value)
  );
  const userIds = Object.keys(requiredVotes.aggOnUser || {}).map((value) =>
    Number(value)
  );
  const hasAssignments =
    entryIds.length > 0 &&
    userIds.length > 0 &&
    requiredVotes.users.length > 0;

  return (
    <div className="min-h-screen bg-slate-100 px-4 pb-8 pt-16 dark:bg-slate-900">
      <div className="mx-auto max-w-6xl">
        <h1 className="mb-6 text-2xl font-bold text-slate-800 dark:text-white">
          Required Votes
        </h1>

        {!hasAssignments ? (
          <div
            className="rounded-xl border border-slate-200 bg-white p-6 dark:border-white/10 dark:bg-slate-800"
            data-testid={TestIds.VotesRequiredEmpty}
          >
            <p className="text-slate-600 dark:text-slate-300">
              No required votes have been generated for this compo yet.
            </p>
            <p className="mt-2 text-slate-500 dark:text-slate-400">
              Generate the assignments first, then revisit this page.
            </p>
          </div>
        ) : (
          <div
            className="overflow-hidden rounded-xl border border-slate-200 bg-white dark:border-white/10 dark:bg-slate-800"
            data-testid={TestIds.VotesRequiredTable}
          >
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-700/50">
                    <th className="sticky left-0 z-10 bg-slate-50 px-4 py-3 text-left text-sm font-medium text-slate-600 dark:bg-slate-700/50 dark:text-slate-300">
                      User
                    </th>
                    {entryIds.map((entryId) => (
                      <th
                        key={`entry-${entryId}`}
                        className="px-2 py-3 text-center text-sm font-medium text-slate-600 dark:text-slate-300"
                      >
                        <RLink
                          to={`/entry/${entryId}`}
                          className="hover:text-blue-500"
                        >
                          {entryId}
                        </RLink>
                      </th>
                    ))}
                    <th className="px-4 py-3 text-center text-sm font-medium text-slate-600 dark:text-slate-300">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {userIds.map((userId) => {
                    const matchingUser = requiredVotes.users.find(
                      (user) => user.id === Number(userId)
                    );
                    const username = matchingUser
                      ? matchingUser.username
                      : `User ${userId}`;
                    const userAssignments =
                      requiredVotes.aggOnUser[Number(userId)] || [];
                    const assignmentCount = userAssignments.length;

                    return (
                      <tr
                        key={`user-${userId}`}
                        className="border-b border-slate-100 last:border-0 dark:border-slate-700/50"
                      >
                        <td className="sticky left-0 z-10 bg-white px-4 py-2 text-sm font-medium text-slate-800 dark:bg-slate-800 dark:text-white">
                          <RLink to={`/user/${username}`}>{username}</RLink>
                        </td>
                        {entryIds.map((entryId) => {
                          const hasAssignment =
                            userAssignments.indexOf(entryId) !== -1;

                          return (
                            <td
                              key={`cell-${userId}-${entryId}`}
                              className="px-2 py-2 text-center"
                            >
                              {hasAssignment ? (
                                <CheckIcon className="mx-auto h-5 w-5 text-emerald-400" />
                              ) : (
                                <XMarkIcon className="mx-auto h-4 w-4 text-slate-400 dark:text-slate-500" />
                              )}
                            </td>
                          );
                        })}
                        <td className="px-4 py-2 text-center">
                          <span className="inline-flex min-w-[2rem] justify-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                            {assignmentCount}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
